import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Dialog, InlineError, Input, Select, Textarea } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useCreateProject, useProjectTemplates, useTemplateBlueprintCounts } from './hooks';
import { projectErrorMessage, templateOptionLabel, updateProject } from './queries';
import { VISIBILITY_LABELS, normalizeCategory } from './types';
import type { ProjectVisibility } from './types';

/**
 * The minimum project-creation form for the access foundation: name,
 * description, visibility, and template identity.
 *
 * The selected template now builds the project: create_project applies its
 * blueprint sections and starter tasks, and Blank Project applies neither.
 * The form itself sends no statuses; the database creates the approved
 * default lifecycle either way.
 */
export function ProjectCreateDialog({
  open,
  onClose,
  categories = [],
  initialCategory = '',
}: {
  open: boolean;
  onClose: () => void;
  /** Known categories offered in the picker (curated plus in use). */
  categories?: string[];
  /** Pre-selected category, e.g. from a category section's own button. */
  initialCategory?: string;
}) {
  const navigate = useNavigate();
  const { client } = useAuth();
  const templates = useProjectTemplates();
  const blueprints = useTemplateBlueprintCounts();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<ProjectVisibility>('members_only');
  const [templateKey, setTemplateKey] = useState('blank');
  const [category, setCategory] = useState(initialCategory);
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  // Each opening adopts the category of the section it was opened from.
  useEffect(() => {
    if (open) {
      setCategory(initialCategory);
    }
  }, [open, initialCategory]);

  const clearFields = useCallback(() => {
    setName('');
    setDescription('');
    setVisibility('members_only');
    setTemplateKey('blank');
    setCategory('');
    setNameError(undefined);
  }, []);

  const creation = useCreateProject((project) => {
    const chosen = category.trim();
    clearFields();
    onClose();
    // The category rides along after creation; a failure here never blocks
    // the person from landing on their new project.
    if (chosen !== '') {
      void updateProject(client, project.id, {
        category: normalizeCategory(chosen, categories),
      }).catch(() => undefined);
    }
    // The created project is loaded fresh on its own page rather than assumed.
    void navigate(`/projects/${project.id}`);
  });

  // Dialog installs a focus trap keyed on `onClose`. Keeping this callback
  // stable stops the trap from tearing down and stealing focus on every
  // keystroke, which otherwise made the form impossible to type into.
  const isPending = creation.isPending;
  const resetMutation = creation.reset;

  const handleClose = useCallback(() => {
    if (isPending) {
      return;
    }
    clearFields();
    resetMutation();
    onClose();
  }, [isPending, clearFields, resetMutation, onClose]);

  function handleSubmit() {
    // Duplicate submission guard: one creation is in flight at a time.
    if (creation.isPending) {
      return;
    }
    if (name.trim() === '') {
      setNameError('Enter a project name.');
      return;
    }
    setNameError(undefined);
    creation.mutate({ name, description, visibility, templateKey });
  }

  // Labels say what each template will build. The dropdown used to be pure
  // metadata, so people picked one as a category label; it now creates
  // sections and tasks, and the option itself is where that has to be said.
  const templateOptions = (templates.data ?? []).map((template) => ({
    value: template.template_key,
    label: templateOptionLabel(template.name, blueprints.data?.[template.template_key]),
  }));

  return (
    <Dialog
      open={open}
      title="New project"
      onClose={handleClose}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={creation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={creation.isPending}>
            {creation.isPending ? 'Creating…' : 'Create project'}
          </Button>
        </>
      }
    >
      <Input
        label="Project name"
        value={name}
        error={nameError}
        onChange={(event) => setName(event.target.value)}
      />
      <Textarea
        label="Description"
        hint="Optional."
        rows={3}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      {categories.length > 0 ? (
        <Select
          label="Category"
          hint="Where this project lives on the Projects page."
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          options={[
            { value: '', label: 'None' },
            ...categories.map((name) => ({ value: name, label: name })),
          ]}
        />
      ) : null}
      <Select
        label="Visibility"
        hint="All TAP makes the project readable by every TAP user. It does not grant editing."
        value={visibility}
        onChange={(event) => setVisibility(event.target.value as ProjectVisibility)}
        options={[
          { value: 'members_only', label: VISIBILITY_LABELS.members_only },
          { value: 'all_tap', label: VISIBILITY_LABELS.all_tap },
        ]}
      />
      <Select
        label="Template"
        hint="Sets up the project's pipeline, fields, sections and starter tasks. Blank Project creates nothing extra."
        value={templateKey}
        disabled={templates.isPending}
        onChange={(event) => setTemplateKey(event.target.value)}
        options={
          templateOptions.length > 0
            ? templateOptions
            : [{ value: 'blank', label: 'Blank Project (clean slate)' }]
        }
      />
      {creation.isError ? (
        <InlineError>{projectErrorMessage(creation.error.message)}</InlineError>
      ) : null}
    </Dialog>
  );
}
